import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, ElementRef } from '@angular/core';
import { Holiday, HolidayType } from 'src/app/shared/models/billing-settings';
import * as moment from 'moment';

interface GridCell {
  date: moment.Moment;
  dayOfWeek: number; // 0=Mon, 6=Sun
  weekIndex: number;
  isWeekend: boolean;
  holiday: Holiday | null;
  isOrgHoliday: boolean; // inherited from org, not project-specific
  isCurrentMonth: boolean;
  dateStr: string; // YYYY-MM-DD
}

@Component({
    selector: 'app-year-grid-calendar',
    templateUrl: './year-grid-calendar.component.html',
    styleUrls: ['./year-grid-calendar.component.scss'],
    standalone: false
})
export class YearGridCalendarComponent implements OnChanges {
  @Input() holidays: Holiday[] = [];
  @Input() orgHolidays: Holiday[] = [];
  @Input() year = new Date().getFullYear();

  @Output() holidayClick = new EventEmitter<{ date: string; holiday: Holiday | null }>();
  @Output() holidayAdd = new EventEmitter<{ date: string }>();
  @Output() holidayEdit = new EventEmitter<{ date: string; holiday: Holiday }>();
  @Output() holidayRemove = new EventEmitter<{ date: string; holiday: Holiday }>();

  grid: GridCell[][] = []; // 7 rows (Mon-Sun) x ~53 cols (weeks)
  monthLabels: { label: string; colStart: number; colSpan: number }[] = [];
  dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  totalWeeks = 0;

  // Popover state
  activeCell: GridCell | null = null;
  popoverPosition = { top: 0, left: 0 };
  showPopover = false;

  holidayTypeColors: Record<HolidayType, string> = {
    holiday: '#1036D6',
    moratorium: '#E02020',
    weather: '#FA6400',
    suspension: '#8B94B3',
  };

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showPopover) {
      this.closePopover();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showPopover && !this.elementRef.nativeElement.contains(event.target)) {
      this.closePopover();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.year || changes.holidays || changes.orgHolidays) {
      this.buildGrid();
    }
  }

  private buildGrid(): void {
    const startOfYear = moment([this.year, 0, 1]);
    const endOfYear = moment([this.year, 11, 31]);

    // Build holiday lookup maps
    const projectHolidayMap = new Map<string, Holiday>();
    (this.holidays || []).forEach(h => projectHolidayMap.set(h.date, h));

    const orgHolidayMap = new Map<string, Holiday>();
    (this.orgHolidays || []).forEach(h => {
      if (!projectHolidayMap.has(h.date)) {
        orgHolidayMap.set(h.date, h);
      }
    });

    // Calculate the week index for Jan 1 (which row/col it starts at)
    // Week starts on Monday (isoWeekday: 1=Mon, 7=Sun)
    const firstDayOfWeek = startOfYear.isoWeekday() - 1; // 0=Mon, 6=Sun

    // Calculate total weeks needed
    const daysInYear = endOfYear.dayOfYear();
    this.totalWeeks = Math.ceil((daysInYear + firstDayOfWeek) / 7);

    // Initialize grid: 7 rows x totalWeeks cols
    this.grid = [];
    for (let row = 0; row < 7; row++) {
      this.grid[row] = [];
    }

    // Fill grid
    const current = startOfYear.clone();
    while (current.isSameOrBefore(endOfYear)) {
      const dayOfWeek = current.isoWeekday() - 1; // 0=Mon, 6=Sun
      const dayOfYear = current.dayOfYear() - 1;
      const weekIndex = Math.floor((dayOfYear + firstDayOfWeek) / 7);
      const dateStr = current.format('YYYY-MM-DD');

      const projectHoliday = projectHolidayMap.get(dateStr) || null;
      const orgHoliday = orgHolidayMap.get(dateStr) || null;

      const cell: GridCell = {
        date: current.clone(),
        dayOfWeek,
        weekIndex,
        isWeekend: dayOfWeek >= 5,
        holiday: projectHoliday || orgHoliday,
        isOrgHoliday: !projectHoliday && !!orgHoliday,
        isCurrentMonth: true,
        dateStr,
      };

      this.grid[dayOfWeek][weekIndex] = cell;
      current.add(1, 'day');
    }

    // Build month labels
    this.buildMonthLabels();
  }

  private buildMonthLabels(): void {
    this.monthLabels = [];
    const startOfYear = moment([this.year, 0, 1]);
    const firstDayOfWeek = startOfYear.isoWeekday() - 1;

    for (let month = 0; month < 12; month++) {
      const firstOfMonth = moment([this.year, month, 1]);
      const lastOfMonth = firstOfMonth.clone().endOf('month');

      const startDayOfYear = firstOfMonth.dayOfYear() - 1;
      const endDayOfYear = lastOfMonth.dayOfYear() - 1;

      const colStart = Math.floor((startDayOfYear + firstDayOfWeek) / 7);
      const colEnd = Math.floor((endDayOfYear + firstDayOfWeek) / 7);

      this.monthLabels.push({
        label: firstOfMonth.format('MMM'),
        colStart,
        colSpan: colEnd - colStart + 1,
      });
    }
  }

  getCellClass(cell: GridCell | undefined): string {
    if (!cell) return 'empty';
    const classes = ['cell'];
    if (cell.isWeekend) classes.push('weekend');
    if (cell.holiday) {
      classes.push('has-holiday');
      classes.push(`type-${cell.holiday.type}`);
      if (cell.isOrgHoliday) classes.push('org-inherited');
    }
    return classes.join(' ');
  }

  getCellColor(cell: GridCell | undefined): string {
    if (!cell) return 'transparent';
    if (cell.holiday) {
      return this.holidayTypeColors[cell.holiday.type] || '#1036D6';
    }
    if (cell.isWeekend) return '#E9ECF4';
    return '#FFFFFF';
  }

  onCellClick(cell: GridCell | undefined, event: MouseEvent): void {
    if (!cell) return;
    event.stopPropagation();

    this.activeCell = cell;
    const target = event.target as HTMLElement;
    const container = target.closest('.year-grid-container') as HTMLElement;
    if (container) {
      this.popoverPosition = {
        top: target.offsetTop + target.offsetHeight + 4,
        left: target.offsetLeft,
      };
    }

    this.showPopover = true;
    this.holidayClick.emit({ date: cell.dateStr, holiday: cell.holiday });
  }

  onAddHoliday(): void {
    if (this.activeCell) {
      this.holidayAdd.emit({ date: this.activeCell.dateStr });
      this.closePopover();
    }
  }

  onEditHoliday(): void {
    if (this.activeCell?.holiday) {
      this.holidayEdit.emit({ date: this.activeCell.dateStr, holiday: this.activeCell.holiday });
      this.closePopover();
    }
  }

  onRemoveHoliday(): void {
    if (this.activeCell?.holiday) {
      this.holidayRemove.emit({ date: this.activeCell.dateStr, holiday: this.activeCell.holiday });
      this.closePopover();
    }
  }

  closePopover(): void {
    this.showPopover = false;
    this.activeCell = null;
  }

  getCellTooltip(cell: GridCell | undefined): string {
    if (!cell || this.showPopover) return '';
    const dateLabel = cell.date.format('ddd, MMM D, YYYY');
    if (cell.holiday) {
      const source = cell.isOrgHoliday ? ' (org)' : '';
      return `${dateLabel}\n${cell.holiday.name} - ${cell.holiday.type}${source}`;
    }
    if (cell.isWeekend) return `${dateLabel} (weekend)`;
    return dateLabel;
  }

  trackByWeek(index: number, _item: any): number {
    return index;
  }

  trackByRow(index: number, _item: any): number {
    return index;
  }
}
